import { Eye, EyeOff } from "lucide-react"
import { type FocusEvent, useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import type { CredentialField } from "@/lib/integration-registry"

function SensitiveInput({
  id,
  value,
  onChange,
  placeholder,
  selectOnFocus,
  masked,
}: {
  id: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  selectOnFocus?: boolean
  masked?: boolean
}) {
  const [visible, setVisible] = useState(false)

  function handleFocus(e: FocusEvent<HTMLInputElement>) {
    if (selectOnFocus) e.target.select()
  }

  return (
    <div className="relative">
      <Input
        id={id}
        type={visible ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleFocus}
        autoComplete="off"
        className="pr-9"
      />
      {!masked && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-foreground absolute top-0 right-0 h-9 w-9"
          onClick={() => setVisible((v) => !v)}
          tabIndex={-1}
          aria-label={visible ? "Hide value" : "Show value"}
        >
          {visible ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </Button>
      )}
    </div>
  )
}

interface ApiKeyCredentialsFormProps {
  fields: CredentialField[]
  values: Record<string, string>
  onChange: (values: Record<string, string>) => void
  idPrefix: string
  mode?: "connect" | "manage"
  // Per-field flag: true when the value shown is the masked placeholder for an
  // already-saved secret (so we hide the reveal toggle).
  maskedKeys?: Record<string, boolean>
}

export function ApiKeyCredentialsForm({
  fields,
  values,
  onChange,
  idPrefix,
  mode = "connect",
  maskedKeys = {},
}: ApiKeyCredentialsFormProps) {
  const isManage = mode === "manage"

  return (
    <div className="space-y-4">
      {fields.map((field) => {
        const id = `${idPrefix}-${field.key}`
        const value = values[field.key] ?? ""
        return (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={id}>{field.label}</Label>
            {field.secret ? (
              <SensitiveInput
                id={id}
                value={value}
                onChange={(v) => onChange({ ...values, [field.key]: v })}
                placeholder={field.placeholder}
                selectOnFocus={isManage}
                masked={maskedKeys[field.key]}
              />
            ) : (
              <Input
                id={id}
                value={value}
                placeholder={field.placeholder}
                autoComplete="off"
                onChange={(e) =>
                  onChange({ ...values, [field.key]: e.target.value })
                }
              />
            )}
            {field.help && (
              <p className="text-muted-foreground text-xs">{field.help}</p>
            )}
          </div>
        )
      })}
    </div>
  )
}
